/**
 * WHICH ROWS BELONG TO THIS CREATIVE SESSION — the one implementation.
 *
 * MOVED HERE 2026-09-09 (V2-AUTONOMOUS-WAVE3-02-ANGLE) from
 * `components/CreativeEngine.jsx`, where it still lived when v2 began sharing
 * it. The move is not tidying: `services/useAngles.js` needs this rule, and
 * importing it from `CreativeEngine.jsx` would make a cycle between v1's
 * 3,400-line module and a hook v2 mounts on its own. The BEHAVIOUR is
 * unchanged — the body below is the original, character for character, and
 * `CreativeEngine.jsx` re-exports this name so every existing caller and test
 * keeps working.
 */

// Rows now carry the `session_id` that produced them. Before that, the only
// scoping any step could do was `created_at >= sessionStartTime`, and that
// timestamp exists ONLY for a brand-new session — so a RESUMED session was
// unscoped on every step, and Creative Elements (which had no scoping at all)
// showed every script the offer had ever had. Live: 13 scripts across four
// sessions, all shown at once.
//
// Three outcomes, kept distinct on purpose (rule 10):
//   mine     — the row names THIS session.
//   theirs   — the row names a DIFFERENT session. Hidden, and counted.
//   unknown  — the row names no session at all. It predates stamping, so it is
//              not "not mine" and must not be silently binned as if it were.
//              Hidden, counted, and said out loud on screen.
export const scopeToSession = (rows, sessionId, { isNewSession, sessionStartTime } = {}) => {
  const list = rows || [];
  if (!sessionId) {
    // No session to scope to. Fall back to the timestamp when there is one,
    // and otherwise show everything rather than inventing a filter.
    if (isNewSession && sessionStartTime) {
      const cutoff = new Date(sessionStartTime);
      return {
        rows: list.filter(r => r.created_at && new Date(r.created_at) >= cutoff),
        hiddenOtherSession: 0,
        hiddenUnstamped: 0,
      };
    }
    return { rows: list, hiddenOtherSession: 0, hiddenUnstamped: 0 };
  }
  const mine = [];
  let hiddenOtherSession = 0;
  let hiddenUnstamped = 0;
  for (const r of list) {
    if (r.session_id === sessionId) mine.push(r);
    else if (r.session_id) hiddenOtherSession += 1;
    else hiddenUnstamped += 1;
  }
  return { rows: mine, hiddenOtherSession, hiddenUnstamped };
};


/**
 * WHICH ROWS TO SHOW, when strict scoping would show nothing.
 *
 * Generalised from `useAngles.visibleAngles` on 2026-09-09 (wave 3 screen 3)
 * so the Hooks step shares the rule rather than carrying a second copy of it.
 * Two copies of one rule against one database eventually disagree — that is
 * the whole reason the step hooks exist.
 *
 * `scopeToSession` alone is not enough for these collections. Almost nothing in
 * production carries a `session_id` yet (angles were stamped from 2026-09-09;
 * 14 of 1,756 copy elements; 2 of 287 scripts), so scoping a RESUMED session
 * strictly would hide the operator's own previous work and show an empty
 * screen. That is the regression the Founder caught on wave 3 screen 1, and it
 * must not be rebuilt one screen at a time.
 *
 *   new session      strict. Starting clean is the point, and its rows carry
 *                    its id from this build onward. What the offer holds is
 *                    counted, not shown.
 *   resumed, stamped strict. This session's rows are known.
 *   resumed, none    FALL BACK to the unstamped rows and say so. An empty
 *                    screen with the answer sitting in the response is a bug,
 *                    not a scoping rule.
 */
export const visibleForSession = (all, sessionId, { isNewSession, sessionStartTime } = {}) => {
  const scoped = scopeToSession(all, sessionId, { isNewSession, sessionStartTime });
  if (!isNewSession && scoped.rows.length === 0 && scoped.hiddenUnstamped > 0) {
    return {
      rows: (all || []).filter((r) => !r.session_id),
      hiddenOtherSession: scoped.hiddenOtherSession,
      hiddenUnstamped: 0,
      predateNote: scoped.hiddenUnstamped,
    };
  }
  return { ...scoped, predateNote: 0 };
};
